import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  CallRouteType,
  Edge,
  Node,
  PhoneNumber,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { ListResponse as OverridesListResponse } from '@weave/schema-gen-ts/dist/schemas/phone/override/v2/override_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { SettingsTenantLocation } from '@frontend/phone-config';
import { breakpoints, useTargetElementContainerQuery } from '@frontend/responsiveness';
import { ConfirmationModal, Modal, Tray } from '@frontend/design-system';
import { trackingId } from '../../../tracking';
import { OverrideSettingTray } from '../../override/override-settings-tray';
import { EditBreakModal } from '../../phone-hours/phone-hours-form/edit-break-modal';
import { ChangeLocationModal } from '../change-location-modal';
import { DeleteCallRouteModal } from '../delete-call-route-modal/delete-call-route-modal';
import { EditExtensionsModal } from '../edit-extensions-modal';
import { EditPhoneNumbersModal } from '../edit-phone-numbers-modal';
import { StepOptions } from '../helpers/add-step-helpers';
import { filterCallRoutesOnly } from '../helpers/call-routes';
import { getSiblingBreakNames } from '../helpers/graph/node-utils';
import { RenameCallRouteModal } from '../rename-call-route-modal';
import { ExtendedCallRoute } from '../types';
import { useCallObjectsData } from '../use-call-objects-data';
import { AddStepPanel } from './add-step-panel/add-step-panel';
import { PhoneTreeAncestors } from './add-step-panel/repeat-step';
import { NodeStateWithAdditionalData, PhoneHoursNodeState } from './add-step-panel/types';
import { CallRouteSidePanelTray } from './call-route-side-panel-tray';
import type { CallRouteModalControls } from './hooks/use-call-route-modals';
import type { UseNodeActionsParams } from './hooks/use-node-actions';
import { PhoneTreeOverrideUsageModal } from './phone-tree-override-usage-modal';
import { WarningModal } from './warning-modal';

/**
 * Processed node type with additional metadata
 */
export type ProcessedNode = CallRouteV1.Types.Node & {
  isPhoneTreeDescendent?: boolean;
  warning?: string;
  isExtendable?: boolean;
};

export interface CallRouteModalsProps {
  // Modal controls
  modals: CallRouteModalControls;

  // Call route data
  extendedCallRoute: ExtendedCallRoute | undefined;
  callRoute: CallRouteV1.Types.ReadIO['output'] | undefined;
  callRouteName: string;
  callRouteId: string;
  isDeviceExtensionRoute: boolean;

  // Phone numbers data
  unallocatedPhoneNumbers: PhoneNumber[];

  // Location data
  settingsTenantLocation: SettingsTenantLocation | undefined;

  // Call objects data
  callObjectsData: ReturnType<typeof useCallObjectsData>;

  // Node action handlers
  processStepAction: (nodeStateWithAdditionalData: NodeStateWithAdditionalData) => void;
  handleDuplicateSubGraph: (params: { nodes: CallRouteV1.Types.Node[]; edges: Edge[] }) => void;
  getEditingNodeInitialData: () => NodeStateWithAdditionalData | undefined;
  getAncestorPhoneTrees: () => PhoneTreeAncestors;
  getNodes: UseNodeActionsParams['getNodes'];
  getEdges: UseNodeActionsParams['getEdges'];

  // Step options
  stepOptions: StepOptions;

  // Processing state
  processingNode: ProcessedNode | undefined;
  processingActionType: 'add' | 'edit' | 'delete' | undefined;
  tempProcessingData: NodeStateWithAdditionalData | undefined;
  tempDuplicateData:
    | {
        nodes: CallRouteV1.Types.Node[];
        edges: Edge[];
        rootNode: CallRouteV1.Types.Node;
      }
    | undefined;

  // Action handlers
  handleEditBreakHourNode: (newName: string) => void;
  handleConfirmEditStep: () => void;
  handleConfirmAddStep: () => void;
  handleConfirmDeleteStep: () => void;
  handleConfirmDuplicateStep: () => void;

  // Side Panel Tray props
  activePanel: string | undefined;
  onPanelClose: () => void;
  onOpenPhoneHoursPanel?: () => void;
  isSingleTypeScope: boolean;
  locationName: string;
  officeHourNodes: Node[];
  onSavePhoneHours: (params: { nodeId: string; nodeUpdate: PhoneHoursNodeState }) => void;
  hoursManagementPermission: boolean;
  triggerPhoneHoursEdit?: boolean;

  // Navigation
  onNavigateToCallRoutes: () => void;

  // Call route save handler (for override tray)
  onSaveCallRoute?: () => Promise<void>;
  callRouteIsSaving?: boolean;

  overrides: OverridesListResponse;
}

/**
 * Component that renders all modals and trays for the call route details page.
 * Extracted to keep the main component cleaner.
 */
export const CallRouteModals = ({
  modals,
  extendedCallRoute,
  callRoute,
  callRouteName,
  callRouteId,
  isDeviceExtensionRoute,
  unallocatedPhoneNumbers,
  settingsTenantLocation,
  callObjectsData,
  processStepAction,
  handleDuplicateSubGraph,
  getEditingNodeInitialData,
  getAncestorPhoneTrees,
  getNodes,
  getEdges,
  stepOptions,
  processingNode,
  processingActionType,
  tempProcessingData,
  tempDuplicateData,
  handleEditBreakHourNode,
  handleConfirmEditStep,
  handleConfirmAddStep,
  handleConfirmDeleteStep,
  handleConfirmDuplicateStep,
  activePanel,
  onPanelClose,
  isSingleTypeScope,
  locationName,
  officeHourNodes,
  onSavePhoneHours,
  hoursManagementPermission,
  triggerPhoneHoursEdit,
  onNavigateToCallRoutes,
  onSaveCallRoute,
  callRouteIsSaving,
  overrides,
}: CallRouteModalsProps) => {
  const { t } = useTranslation('phone');
  const [isAddStepCalendarViewOpen, setIsAddStepCalendarViewOpen] = useState(false);
  const [isSidePanelCalendarViewOpen, setIsSidePanelCalendarViewOpen] = useState(false);
  const mainContentElement = useMemo(() => document.querySelector('#main-content') as HTMLElement, []);
  const [isMediumMode] = useTargetElementContainerQuery({
    maxWidth: breakpoints.medium.max,
    element: mainContentElement,
  });

  // Reset calendar view to collapsed whenever the Add Step tray closes.
  useEffect(() => {
    if (!modals.addStep.modalProps.show) {
      setIsAddStepCalendarViewOpen(false);
    }
  }, [modals.addStep.modalProps.show]);

  return (
    <>
      {/* Edit Phone Numbers Modal */}
      {extendedCallRoute && settingsTenantLocation && (
        <Modal {...modals.editPhoneNumbers.modalProps} minWidth={600}>
          <EditPhoneNumbersModal
            onClose={modals.editPhoneNumbers.modalProps.onClose}
            callRoute={extendedCallRoute}
            tenantLocation={settingsTenantLocation}
            isDeviceExtensionRoute={isDeviceExtensionRoute}
            unallocatedPhoneNumbers={unallocatedPhoneNumbers ?? []}
          />
        </Modal>
      )}

      {/* Edit Extensions Modal */}
      {extendedCallRoute && settingsTenantLocation && (
        <Modal {...modals.editExtensions.modalProps} minWidth={600}>
          <EditExtensionsModal
            onClose={modals.editExtensions.modalProps.onClose}
            callRoute={extendedCallRoute}
            tenantLocation={settingsTenantLocation}
          />
        </Modal>
      )}

      {/* Change Location Modal */}
      {extendedCallRoute && settingsTenantLocation && (
        <Modal {...modals.changeLocation.modalProps} minWidth={580}>
          <ChangeLocationModal
            onClose={modals.changeLocation.modalProps.onClose}
            callRoute={extendedCallRoute}
            settingsTenantLocation={settingsTenantLocation}
          />
        </Modal>
      )}

      {/* Rename Call Route Modal */}
      {extendedCallRoute && (
        <Modal {...modals.renameCallRoute.modalProps} minWidth={400}>
          <RenameCallRouteModal onClose={modals.renameCallRoute.modalProps.onClose} callRoute={extendedCallRoute} />
        </Modal>
      )}

      {/* Delete Call Route Modal */}
      {modals.deleteModal.modalProps.show && (
        <DeleteCallRouteModal
          deleteModalProps={modals.deleteModal.modalProps}
          callRouteId={callRouteId}
          callRouteName={callRouteName}
          phoneNumbers={extendedCallRoute?.phoneNumbers ?? []}
          callRoutes={filterCallRoutesOnly(callObjectsData?.routes)}
          department={callObjectsData?.departments.find((dept: any) => dept.callRouteId === callRouteId)}
          onSuccess={onNavigateToCallRoutes}
        />
      )}

      {/* Override Settings Tray */}
      <OverrideSettingTray
        mountTarget='#call-route-body-section'
        override={extendedCallRoute?.callRoutePhoneOverride}
        showUnsavedPhoneTreeOptions={true}
        onSaveCallRoute={onSaveCallRoute}
        modalControls={modals.editOverride}
        isLoading={callRouteIsSaving}
        callRouteNodes={getNodes()}
        callRouteEdges={getEdges()}
      />

      {/* Add Step Tray */}
      <Tray
        mountTarget='#call-route-page-body-container'
        width='xlarge'
        {...modals.addStep.modalProps}
        css={css`
          ${isAddStepCalendarViewOpen &&
          `
            border-left: solid var(--fabric-color-neutral-10);
            overflow-y: scroll;
            max-width: ${!isMediumMode ? 'calc(100% - 272px)' : '700px'};
          `}
          transition: max-width 800ms ease;
          padding: 0;
        `}
      >
        {modals.addStep.modalProps.show && !!callRoute?.callRoute && (
          <AddStepPanel
            onClose={modals.addStep.modalProps.onClose}
            onAddStep={processStepAction}
            onDuplicateSubGraph={handleDuplicateSubGraph}
            initialState={getEditingNodeInitialData()}
            callObjectsData={callObjectsData}
            callRoute={callRoute?.callRoute}
            isDeviceExtensionRoute={isDeviceExtensionRoute}
            stepOptions={stepOptions}
            getAncestorPhoneTrees={getAncestorPhoneTrees}
            processingNodeId={processingNode?.id}
            isCalendarViewOpen={isAddStepCalendarViewOpen}
            onCalendarViewChange={setIsAddStepCalendarViewOpen}
            omitBackActions={
              activePanel === 'PhoneHours' && processingNode?.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS
            }
          />
        )}
      </Tray>

      {/* Force Reset Confirmation Modal */}
      <ConfirmationModal
        {...modals.forceReset.modalProps}
        maxWidth={500}
        title={t('{{callRouteName}} Call Route Has Changed', { callRouteName: callRoute?.callRoute.name })}
        message={t(
          'This Call Route was updated elsewhere since your last visit. The edits you made here have been replaced to reflect the most recent changes.'
        )}
        confirmLabel={t('View Call Route')}
        confirmTrackingId={trackingId({
          context: 'setting',
          feature: isDeviceExtensionRoute ? 'device-extension-route' : 'call-route',
          details: 'force-reset::confirm-btn',
        })}
        cancelLabel=''
        onConfirm={modals.forceReset.modalProps.onClose}
      />

      {/* Edit Break Modal */}
      <Modal {...modals.editBreak.modalProps} minWidth={400}>
        {!!processingNode && modals.editBreak.modalProps.show && (
          <EditBreakModal
            name={processingNode?.callObject?.primitiveName ?? ''}
            onCancel={modals.editBreak.modalProps.onClose}
            onDone={handleEditBreakHourNode}
            existingBreakNames={getSiblingBreakNames(processingNode?.id ?? '', getNodes(), getEdges())}
          />
        )}
      </Modal>

      {/* Warning Modal */}
      {processingActionType && (
        <WarningModal
          modalProps={modals.confirmation.modalProps}
          nodeName={processingActionType === 'delete' ? processingNode?.callObject?.primitiveName : ''}
          stepType={
            tempDuplicateData
              ? tempDuplicateData.rootNode.type
              : processingActionType === 'add'
              ? tempProcessingData?.nodeState.type
              : processingNode?.type
          }
          routeType={
            isDeviceExtensionRoute ? CallRouteType.CALL_ROUTE_TYPE_EXTENSION : CallRouteType.CALL_ROUTE_TYPE_CALL_ROUTE
          }
          stepActionType={processingActionType}
          onConfirm={() => {
            if (tempDuplicateData) {
              handleConfirmDuplicateStep();
            } else if (processingActionType === 'delete') {
              handleConfirmDeleteStep();
            } else if (processingActionType === 'edit') {
              handleConfirmEditStep();
            } else if (processingActionType === 'add') {
              handleConfirmAddStep();
            }
          }}
          changingStepType={
            tempDuplicateData
              ? tempDuplicateData.rootNode.type
              : processingActionType === 'edit'
              ? tempProcessingData?.nodeState.type
              : undefined
          }
        />
      )}

      {/* Side Panel Tray */}
      <CallRouteSidePanelTray
        activePanel={activePanel}
        onPanelClose={onPanelClose}
        sidePanelModal={modals.sidePanel}
        editPhoneNumbersModal={modals.editPhoneNumbers}
        editExtensionsModal={modals.editExtensions}
        editOverrideModal={modals.editOverride}
        changeLocationModal={modals.changeLocation}
        extendedCallRoute={extendedCallRoute}
        isDeviceExtensionRoute={isDeviceExtensionRoute}
        isSingleTypeScope={isSingleTypeScope}
        locationName={locationName}
        locationId={settingsTenantLocation?.locationId ?? ''}
        officeHourNodes={officeHourNodes}
        onSavePhoneHours={onSavePhoneHours}
        hoursManagementPermission={hoursManagementPermission}
        triggerPhoneHoursEdit={triggerPhoneHoursEdit}
        isCalendarViewOpen={isSidePanelCalendarViewOpen}
        onCalendarViewChange={setIsSidePanelCalendarViewOpen}
        processingNodeId={processingNode?.id}
      />

      {modals.phoneTreeOverrideUsage.modalProps.show && (
        <PhoneTreeOverrideUsageModal
          modalProps={modals.phoneTreeOverrideUsage.modalProps}
          processingNodeId={processingNode?.id ?? ''}
          nodes={getNodes()}
          edges={getEdges()}
          overrides={overrides}
        />
      )}
    </>
  );
};
